import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { ALARM_INTERVAL_UNITS } from './alarm-interval';
import { MAX_ALARM_INTEGER } from './alarm-rule-validation';

const FloatingValidationCallout = ({ inputId, id, testId, children }) => {
  const calloutRef = useRef(null);

  useLayoutEffect(() => {
    const input = document.getElementById(inputId);
    const callout = calloutRef.current;
    if (!input || !callout) return undefined;
    const body = input.closest('.modal-body');
    const updatePosition = () => {
      const rect = input.getBoundingClientRect();
      const bodyRect = body?.getBoundingClientRect();
      const visible = rect.bottom > (bodyRect?.top ?? 0)
        && rect.top < (bodyRect?.bottom ?? window.innerHeight);
      callout.style.width = `${Math.min(280, rect.width, window.innerWidth - 16)}px`;
      callout.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - callout.offsetWidth - 8))}px`;
      callout.style.top = `${Math.max(8, rect.top - callout.offsetHeight - 8)}px`;
      callout.style.visibility = visible ? 'visible' : 'hidden';
    };
    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(input);
    observer.observe(callout);
    if (body) observer.observe(body);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [inputId, children]);

  return createPortal(
    <p ref={calloutRef} id={id} className="validation-callout alarm-rule-floating-validation" role="alert" data-testid={testId}>{children}</p>,
    document.body,
  );
};

FloatingValidationCallout.propTypes = {
  inputId: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  testId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const SubscriberSelection = ({ prefix, subscriberPrefix, users, subscriberIds, onToggle }) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      const dropdown = dropdownRef.current;
      if (dropdown?.open && !dropdown.contains(event.target)) dropdown.open = false;
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="form-group">
      <div className="control-label" id={`${prefix}-subscribers-label`}>Subscribers</div>
      <details ref={dropdownRef} className="alarm-rule-form__subscribers-dropdown" aria-labelledby={`${prefix}-subscribers-label`} data-testid={`${subscriberPrefix}-subscribers-dropdown`}>
        <summary className="form-control" data-testid={`${prefix}-subscribers-toggle`}>{subscriberIds.length} selected <i className="fas fa-chevron-down" aria-hidden="true" /></summary>
        <div className="alarm-rule-form__subscribers" data-testid={`${subscriberPrefix}-subscribers-list`}>
          {users.map(user => (
            <label className="alarm-rule-form__subscriber-row" key={user.id}>
              <input type="checkbox" checked={subscriberIds.includes(Number(user.id))} onChange={() => onToggle(user.id)} data-testid={`${subscriberPrefix}-subscriber-checkbox-${user.id}`} />
              {user.name}
            </label>
          ))}
          {!users.length && <p className="help-block" data-testid={`${prefix}-subscribers-empty`}>No users available.</p>}
        </div>
      </details>
    </div>
  );
};

SubscriberSelection.propTypes = {
  prefix: PropTypes.string.isRequired,
  subscriberPrefix: PropTypes.string.isRequired,
  users: PropTypes.array.isRequired,
  subscriberIds: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const RuleNameField = ({ mode, prefix, state }) => {
  const { form, changeField, touchField, getFieldError } = state;
  const nameError = getFieldError('ruleName');
  return (
    <div className="form-group">
      <label htmlFor={`${mode}RuleName`}>Rule Name</label>
      <div className="alarm-rule-form__validation-anchor">
        <input type="text" id={`${mode}RuleName`} className="form-control" value={form.ruleName ?? ''} required
          disabled={mode === 'edit'}
          onBlur={() => touchField('ruleName')}
          aria-invalid={!!nameError} aria-describedby={nameError ? `${prefix}-name-error` : undefined}
          onChange={event => changeField('ruleName', event.target.value)} data-testid={`${prefix}-name-input`} />
        {nameError && <FloatingValidationCallout inputId={`${mode}RuleName`} id={`${prefix}-name-error`} testId={`${prefix}-name-error`}>{nameError}</FloatingValidationCallout>}
      </div>
    </div>
  );
};

RuleNameField.propTypes = {
  mode: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  state: PropTypes.object.isRequired,
};

const RuleTypeField = ({ mode, prefix, state, saving }) => {
  const { form, changeField, touchField, touched, ruleTypes, ruleTypesLoading, ruleTypesError, getRuleTypeError, retryRuleTypes } = state;
  const typeError = getRuleTypeError(!!touched.ruleTypeId);
  return (
    <div className="form-group" data-testid={`${prefix}-type-field`}>
      <label htmlFor={`${mode}RuleType`}>Type</label>
      <div className="alarm-rule-form__type-select-wrap select-with-chevron alarm-rule-form__validation-anchor">
        <select id={`${mode}RuleType`} name="ruleTypeId" className="form-control" required value={form.ruleTypeId ?? ''}
          onChange={event => changeField('ruleTypeId', event.target.value === '' ? null : Number(event.target.value))} disabled={ruleTypesLoading || saving}
          onBlur={() => touchField('ruleTypeId')}
          aria-invalid={!!typeError} aria-describedby={`${mode}RuleTypeError ${mode}RuleTypeLoading`} data-testid={`${prefix}-type-select`}>
          <option value="">Select Type</option>
          {ruleTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
        </select>
        <i className="fas fa-chevron-down" aria-hidden="true" />
        {typeError && <FloatingValidationCallout inputId={`${mode}RuleType`} id={`${mode}RuleTypeError`} testId={`${prefix}-type-error`}>{typeError}</FloatingValidationCallout>}
      </div>
      {ruleTypesLoading && <output id={`${mode}RuleTypeLoading`} className="help-block" data-testid={`${prefix}-type-loading`}>Loading rule types...</output>}
      {!ruleTypesLoading && (ruleTypesError || !ruleTypes.length) && (
        <button type="button" className="btn btn-secondary" disabled={saving} onClick={retryRuleTypes} data-testid={`${prefix}-type-retry-button`}>
          <i className="fas fa-sync-alt fa-fw" aria-hidden="true" /> Retry
        </button>
      )}
    </div>
  );
};

RuleTypeField.propTypes = {
  mode: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  state: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
};

const AlarmRuleCriteriaSettings = ({ mode, state, dataWindowControl, criteriaLoading, intervalTooLong }) => {
  const { form, changeField, touchField, getFieldError } = state;
  const editing = mode === 'edit';
  const prefix = `alarm-rule-${mode}`;
  const numericValue = event => event.target.value === '' ? null : Number(event.target.value);
  const thresholdError = getFieldError('alarmThresholdCount');
  const intervalError = getFieldError('alarmIntervalValue');
  const intervalWindowError = !criteriaLoading && intervalTooLong;
  const intervalWindowErrorId = intervalWindowError ? `${prefix}-interval-exceeds-data-window-error` : undefined;
  const intervalErrorId = intervalError ? `${prefix}-interval-error` : intervalWindowErrorId;
  return (
    <div className="alarm-rule-form__criteria-settings" data-testid={`${prefix}-criteria-settings`}>
      <div className="form-group">
        <label htmlFor={`${mode}AlarmThresholdCount`} title="How many matching event occurrences are required to trigger an alarm, e.g. 3">Threshold Count</label>
        <div className="alarm-rule-form__validation-anchor">
          <input type="number" id={`${mode}AlarmThresholdCount`} className="form-control alarm-rule-form__count" value={form.alarmThresholdCount ?? ''}
            onChange={event => changeField('alarmThresholdCount', numericValue(event))} onBlur={() => touchField('alarmThresholdCount')}
            placeholder="e.g. 3" required min="1" max={MAX_ALARM_INTEGER} step="1"
            aria-invalid={!!thresholdError} aria-describedby={thresholdError ? `${prefix}-threshold-error` : undefined}
            title="How many matching event occurrences are required to trigger an alarm, e.g. 3" data-testid={`${prefix}-threshold-count`} />
          {thresholdError && <p id={`${prefix}-threshold-error`} className="text-danger alarm-rule-form__validation-error" role="alert" data-testid={`${prefix}-threshold-error`}>{thresholdError}</p>}
        </div>
      </div>
      <div className="form-group">
        <label htmlFor={`${mode}AlarmIntervalValue`} title="The occurrence window used to count matching events toward the alarm threshold, e.g. 15">within</label>
        <div className="alarm-rule-form__interval alarm-rule-form__validation-anchor">
          <input type="number" id={`${mode}AlarmIntervalValue`} className="form-control" value={form.alarmIntervalValue ?? ''}
            onChange={event => changeField('alarmIntervalValue', numericValue(event))} onBlur={() => touchField('alarmIntervalValue')}
            placeholder="e.g. 15" required min="0" step="any"
            aria-invalid={!!intervalErrorId} aria-describedby={intervalErrorId}
            title="The occurrence window used to count matching events toward the alarm threshold, e.g. 15" data-testid={`${prefix}-interval-value`} />
          <span className="alarm-rule-form__select-wrap select-with-chevron">
            <select id={`${mode}AlarmIntervalUnit`} className="form-control" value={form.alarmIntervalUnit}
              onChange={event => changeField('alarmIntervalUnit', event.target.value)}
              aria-label="Alarm interval time unit" title="Time unit for the sliding window" data-testid={`${prefix}-interval-unit`}>
              {ALARM_INTERVAL_UNITS.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
            </select>
            <i className="fas fa-chevron-down" aria-hidden="true" />
          </span>
          {intervalErrorId && <p id={intervalErrorId} className="text-danger alarm-rule-form__validation-error" role="alert" data-testid={intervalErrorId}>
            {intervalError || 'Must not exceed the data window.'}
          </p>}
        </div>
      </div>
      <div className="form-group alarm-rule-form__data-window" data-testid={`${prefix}-data-window`}>
        <div className={editing ? 'alarm-rule-form__data-window-control' : undefined}>
          <div className="control-label">Data Window</div>
          {dataWindowControl}
          <p className={editing ? 'help-block' : 'help-block-rule-create'} data-testid={`${prefix}-data-window-help`}>
            {editing ? 'Rules are evaluated every hour.' : 'Rules are evaluated using the selected data window every hour.'}
          </p>
        </div>
      </div>
    </div>
  );
};

AlarmRuleCriteriaSettings.propTypes = {
  mode: PropTypes.string.isRequired,
  state: PropTypes.object.isRequired,
  dataWindowControl: PropTypes.node,
  criteriaLoading: PropTypes.bool.isRequired,
  intervalTooLong: PropTypes.bool.isRequired,
};

export const AlarmRuleForm = ({ mode, state, saving, submitError, disabledReason, onSubmit, onCancel, entityId, dataWindowControl, intervalTooLong, dataWindowTooLarge, criteriaLoading = false, criteriaChanged = false, onRunReport, reportDisabledReason = '', cancelConfirmation = null, reportContent = null, children }) => {
  const { form, changeField, touchField, getFieldError, users, subscriberIds, toggleSubscriber } = state;
  const descriptionError = getFieldError('description');
  const ownerError = getFieldError('ownerText');
  const editing = mode === 'edit';
  const prefix = `alarm-rule-${mode}`;
  const subscriberPrefix = editing ? 'alarm-rule' : prefix;
  const submitIcon = editing ? 'fa-save' : 'fa-plus';
  const buttonIcon = saving ? 'fa-spinner fa-pulse' : submitIcon;
  const StatusIcon = form.isActive ? ToggleOnIcon : ToggleOffIcon;
  const cancelDisabled = editing && (saving || !!cancelConfirmation);
  const cancelButtonRef = useRef(null);
  const hadCancelConfirmation = useRef(false);
  useEffect(() => {
    if (hadCancelConfirmation.current && !cancelConfirmation) cancelButtonRef.current?.focus();
    hadCancelConfirmation.current = !!cancelConfirmation;
  }, [cancelConfirmation]);
  const criteriaSettings = (
    <AlarmRuleCriteriaSettings mode={mode} state={state} dataWindowControl={dataWindowControl}
      criteriaLoading={criteriaLoading} intervalTooLong={intervalTooLong} />
  );

  return (
    <div className={`alarm-rule-form-modal${editing ? ' alarm-rule-form-modal--edit' : ''}`} data-testid={`${prefix}-modal`}>
      <div className="modal-header">
        <h3 className="modal-title">{editing ? 'Edit' : 'Create'} Alarm Rule</h3>
        <button type="button" className="close" aria-label="close" onClick={onCancel} disabled={cancelDisabled}
          data-testid={editing ? 'alarm-rule-edit-close-button' : 'alarm-rule-create-close-button'}><span aria-hidden="true">x</span></button>
      </div>
      <div className="modal-body" data-testid={`${prefix}-modal-body`}>
        <form id={`${prefix}-form`} noValidate onSubmit={event => { event.preventDefault(); onSubmit(); }} data-testid={`${prefix}-form`}>
          <fieldset disabled={cancelDisabled} inert={cancelDisabled ? '' : undefined} data-testid={`${prefix}-fields`}>
          <div className="alarm-rule-form__details-row">
            <div className="alarm-rule-form__metadata-row" data-testid={`${prefix}-metadata-fields`}>
              {editing && (
                <div className="form-group" data-testid="alarm-rule-edit-id-field">
                  <label htmlFor="editRuleId">Rule ID</label>
                  <input type="text" id="editRuleId" className="form-control" value={entityId ?? ''} disabled data-testid="alarm-rule-edit-id" />
                </div>
              )}
              <RuleNameField mode={mode} prefix={prefix} state={state} />
              <RuleTypeField mode={mode} prefix={prefix} state={state} saving={saving} />
              <div className="form-group">
                <label htmlFor={`${mode}RuleDescription`}>Description</label>
                <div className="alarm-rule-form__validation-anchor">
                  <textarea id={`${mode}RuleDescription`} className="form-control" rows="2" value={form.description ?? ''}
                    onChange={event => changeField('description', event.target.value)} onBlur={() => touchField('description')}
                    aria-invalid={!!descriptionError} aria-describedby={descriptionError ? `${prefix}-description-error` : undefined}
                    data-testid={`${prefix}-description-input`} />
                  {descriptionError && <FloatingValidationCallout inputId={`${mode}RuleDescription`} id={`${prefix}-description-error`} testId={`${prefix}-description-error`}>{descriptionError}</FloatingValidationCallout>}
                </div>
              </div>
            </div>
            <div className="alarm-rule-form__administration-row" data-testid={`${prefix}-administration-fields`}>
              <div className="form-group">
                <label htmlFor={`${mode}RuleOwner`}>Owner</label>
                <div className="alarm-rule-form__validation-anchor">
                  <input type="text" id={`${mode}RuleOwner`} className="form-control" value={form.ownerText ?? ''} placeholder="Owner name (free text)"
                    onChange={event => changeField('ownerText', event.target.value)} onBlur={() => touchField('ownerText')}
                    aria-invalid={!!ownerError} aria-describedby={ownerError ? `${prefix}-owner-error` : undefined}
                    data-testid={editing ? 'alarm-rule-owner-input' : `${prefix}-owner-input`} />
                  {ownerError && <FloatingValidationCallout inputId={`${mode}RuleOwner`} id={`${prefix}-owner-error`} testId={`${prefix}-owner-error`}>{ownerError}</FloatingValidationCallout>}
                </div>
              </div>
              <SubscriberSelection prefix={prefix} subscriberPrefix={subscriberPrefix} users={users} subscriberIds={subscriberIds} onToggle={toggleSubscriber} />
              <div className="form-group">
                <label htmlFor={`${mode}StatusToggle`}>Status</label>
                <div className="alarm-rule-form__status">
                  <button type="button" id={`${mode}StatusToggle`} className="btn btn-secondary" title="Set active/inactive rule status"
                    onClick={() => changeField('isActive', !form.isActive)} aria-pressed={form.isActive} data-testid={`${prefix}-status-toggle`}>
                    <StatusIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                  </button>
                  <span className={`alarm-rule-form__status-badge ${form.isActive ? 'is-active' : 'is-inactive'}`}>{form.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
          </div>
          {editing ? (
            <section className="alarm-rule-edit-criteria" data-testid="alarm-rule-edit-criteria">
              <h4>Rule Criteria</h4>{criteriaSettings}{children}
            </section>
          ) : <>{criteriaSettings}{children}</>}
          </fieldset>
        </form>
        {cancelConfirmation}
        <div hidden={!!cancelConfirmation} inert={saving ? '' : undefined} data-testid={`${prefix}-report-area`}>
          {reportContent}
        </div>
        {submitError && <div className="alert alert-danger" role="alert" data-testid={`${prefix}-submit-error`}><i className="fas fa-exclamation-circle" aria-hidden="true" /> {submitError}</div>}
      </div>
      <div className="modal-footer" data-testid={`${prefix}-modal-footer`}>
        {editing && criteriaChanged && (
          <p className="help-block alarm-rule-form__version-warning" data-testid="alarm-rule-edit-version-warning">
            <i className="fas fa-info-circle" aria-hidden="true" data-testid="alarm-rule-edit-version-warning-icon" />{' '}
            Saving creates a new rule version and archives the current one.
          </p>
        )}
        {editing && disabledReason && !saving && !dataWindowTooLarge && (
          <p className="help-block" aria-live="polite" data-testid="alarm-rule-edit-save-reason">{disabledReason}</p>
        )}
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={cancelDisabled} ref={cancelButtonRef} data-testid={`${prefix}-cancel-button`}><i className="fas fa-times fa-fw" aria-hidden="true" /> Cancel</button>
        {onRunReport && (
          <span className="alarm-rule-form__button-hint" title={reportDisabledReason} data-testid="alarm-rule-edit-run-report-button-hint">
            <button type="button" className="btn btn-secondary" disabled={!!reportDisabledReason || !!cancelConfirmation} onClick={onRunReport} data-testid="alarm-rule-edit-run-report-button">
              <i className="fas fa-chart-bar fa-fw" aria-hidden="true" /> Run Report
            </button>
          </span>
        )}
        <span className="alarm-rule-form__button-hint" title={disabledReason} data-testid={`${prefix}-save-button-hint`}>
          <button type="submit" form={`${prefix}-form`} className="btn btn-primary" disabled={!!disabledReason || !!cancelConfirmation} data-testid={`${prefix}-save-button`}>
            <i className={`fas ${buttonIcon} fa-fw`} aria-hidden="true" /> {editing ? 'Save' : 'Create'}
          </button>
        </span>
      </div>
    </div>
  );
};

AlarmRuleForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  state: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  submitError: PropTypes.string,
  disabledReason: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  entityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataWindowControl: PropTypes.node,
  intervalTooLong: PropTypes.bool.isRequired,
  dataWindowTooLarge: PropTypes.bool.isRequired,
  criteriaLoading: PropTypes.bool,
  criteriaChanged: PropTypes.bool,
  onRunReport: PropTypes.func,
  reportDisabledReason: PropTypes.string,
  cancelConfirmation: PropTypes.node,
  reportContent: PropTypes.node,
  children: PropTypes.node,
};